import { backgroundOrange, orange } from '@/theme/colors'
import { PendingAttachmentPicker } from '@components/Attachments'
import { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { EventCategory } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import EventForm from './EventForm'
import { ButtonComponent, ButtonGroupComponent } from './FormComponents'

interface AddEventModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (
		title: string,
		description: string,
		category: EventCategory | undefined,
		location: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		} | undefined,
		links: string[],
		startTime?: string | null,
		createdFromTripIdeaId?: number,
		pendingFiles?: File[],
	) => void
	dayName: string
	initialValues?: {
		title?: string
		description?: string
		category?: EventCategory
		locationPrimaryText?: string
		links?: string[]
		startTime?: string
		createdFromTripIdeaId?: number
	}
}

const AddEventModal: React.FC<AddEventModalProps> = ({ isOpen, onClose, onSubmit, dayName, initialValues }) => {
	const [title, setTitle] = useState('')
	const [description, setDescription] = useState('')
	const [category, setCategory] = useState<EventCategory | undefined>('Other')
	const [locationPrimaryText, setLocationPrimaryText] = useState('')
	const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(null)
	const [links, setLinks] = useState<string[]>([])
	const [startTime, setStartTime] = useState<string>('')
	const [createdFromTripIdeaId, setCreatedFromTripIdeaId] = useState<number | undefined>(undefined)
	const [pendingFiles, setPendingFiles] = useState<File[]>([])

	// Set initial values when modal opens with pre-filled data
	useEffect(() => {
		if (isOpen && initialValues) {
			setTitle(initialValues.title || '')
			setDescription(initialValues.description || '')
			setCategory(initialValues.category || 'Other')
			setLocationPrimaryText(initialValues.locationPrimaryText || '')
			setLinks(initialValues.links || [])
			setStartTime(initialValues.startTime || '')
			setCreatedFromTripIdeaId(initialValues.createdFromTripIdeaId)
		}
	}, [isOpen, initialValues])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (title.trim()) {
			const location = selectedPlace ? {
				fullText: selectedPlace.fullText,
				primaryText: selectedPlace.primaryText,
				secondaryText: selectedPlace.secondaryText || undefined,
				googlePlaceId: selectedPlace.placeId,
				googleTypes: selectedPlace.types,
			} : locationPrimaryText.trim() ? {
				fullText: locationPrimaryText.trim(),
				primaryText: locationPrimaryText.trim(),
				googleTypes: [],
			} : undefined

			onSubmit(title.trim(), description.trim(), category, location, links, startTime || null, createdFromTripIdeaId, pendingFiles.length > 0 ? pendingFiles : undefined)
			setTitle('')
			setDescription('')
			setCategory('Other')
			setLocationPrimaryText('')
			setSelectedPlace(null)
			setLinks([])
			setStartTime('')
			setCreatedFromTripIdeaId(undefined)
			setPendingFiles([])
			onClose()
		}
	}

	const handleCancel = () => {
		setTitle('')
		setDescription('')
		setCategory('Other')
		setLocationPrimaryText('')
		setSelectedPlace(null)
		setLinks([])
		setStartTime('')
		setCreatedFromTripIdeaId(undefined)
		setPendingFiles([])
		onClose()
	}

	const handleLocationSelect = (place: PlaceSuggestion) => {
		setSelectedPlace(place)

		// When location is set, attempt to prefill name as well
		// Get the old location name from selectedPlace or locationPrimaryText
		const oldLocationName = (selectedPlace?.primaryText ?? selectedPlace?.fullText ?? locationPrimaryText ?? '')
			.substring(0, 75)
			.trim()

		// If title is empty or matches the old location name, update it with the new location
		const trimmedTitle = title.trim()
		if (trimmedTitle === '' || trimmedTitle === oldLocationName) {
			// Max length on title is 75 (same as iOS)
			const newTitle = (place.primaryText ?? place.fullText)
				.substring(0, 75)
				.trim()
			setTitle(newTitle)
		}
	}

	const formatDate = (dateString: string): string => {
		const date = new Date(dateString + 'T00:00:00') // Ensure local timezone
		if (Number.isNaN(date.getTime())) {
			return dateString // Fallback to original if invalid
		}
		return date.toLocaleDateString('en-US', {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
		})
	}

	const handleTitleChange = (newTitle: string) => {
		setTitle(newTitle)
	}

	const handleDescriptionChange = (newDescription: string) => {
		setDescription(newDescription)
	}

	const handleCategoryChange = (newCategory: EventCategory | undefined) => {
		setCategory(newCategory)
	}

	const handleStartTimeChange = (newStartTime: string) => {
		setStartTime(newStartTime)
	}

	const handleLocationPrimaryTextChange = (newLocationPrimaryText: string) => {
		setLocationPrimaryText(newLocationPrimaryText)
		setSelectedPlace(null)
	}

	const handleLinksChange = (newLinks: string[]) => {
		setLinks(newLinks)
	}

	const formId = 'add-event-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title={`Add Event to ${formatDate(dayName)}`}
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			closeButtonColor={orange}
			footer={
				<ButtonGroupComponent>
					<ButtonComponent type="button" variant="secondary" onClick={handleCancel}>
						Cancel
					</ButtonComponent>
					<ButtonComponent type="submit" form={formId} variant="primary" disabled={!title.trim()}>
						Add Event
					</ButtonComponent>
				</ButtonGroupComponent>
			}
		>
		<EventForm
			formId={formId}
			title={title}
			description={description}
			category={category}
			locationPrimaryText={locationPrimaryText}
			locationSecondaryText={selectedPlace?.secondaryText || undefined}
			links={links}
				startTime={startTime}
				onTitleChange={handleTitleChange}
				onDescriptionChange={handleDescriptionChange}
				onCategoryChange={handleCategoryChange}
				onLocationPrimaryTextChange={handleLocationPrimaryTextChange}
				onLocationSelect={handleLocationSelect}
				onLinksChange={handleLinksChange}
				onStartTimeChange={handleStartTimeChange}
				onSubmit={handleSubmit}
				attachmentSlot={
					<PendingAttachmentPicker files={pendingFiles} onChange={setPendingFiles} />
				}
			/>
		</Modal>
	)
}

export default AddEventModal
